import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Maximize2, Building2, CheckCircle2, ArrowRight, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { formatArea, formatBudgetRange, propertyTypeLabel, statusLabel } from '@/lib/format';
import { SITE } from '@/lib/site';

export default function PropertyDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/properties/${slug}`)
      .then((res) => { setData(res.data); document.title = `${res.data.title} | Cespedis`; })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in your name, email, and phone.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leads', {
        ...form,
        property_type: data?.property_type,
        requirement: 'occupier_search',
        source_page: `property/${data?.slug || data?.id}`,
      });
      toast.success('Enquiry sent. Our team will reach out shortly.');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="pt-32 pb-20"><div className="mx-auto max-w-7xl px-4 animate-pulse"><div className="h-72 w-full rounded-2xl bg-slate-100" /><div className="mt-6 h-8 w-2/3 rounded bg-slate-100" /></div></div>;
  }
  if (!data) {
    return (
      <div className="pt-32 pb-20"><div className="mx-auto max-w-3xl px-4 text-center">
        <h1 className="font-display text-3xl font-semibold text-[#144726]">Property not found</h1>
        <p className="mt-3 text-slate-600">The property you are looking for may have been leased or removed.</p>
        <Link to="/properties"><Button className="mt-6 bg-[#144726] text-white"><ArrowLeft className="mr-2 h-4 w-4" /> Back to properties</Button></Link>
      </div></div>
    );
  }

  const img = data.images?.[activeImg] || data.images?.[0];

  return (
    <div data-testid="property-detail-page">
      <section className="pt-28 pb-10 md:pt-32 md:pb-12 bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link to="/properties" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#144726]"><ArrowLeft className="h-4 w-4" /> Back to properties</Link>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Badge className="rounded-md bg-[#144726] text-white border-0">{propertyTypeLabel(data.property_type)}</Badge>
            <Badge className="rounded-md bg-[#C6A35B] text-[#144726] border-0">{statusLabel(data.status)}</Badge>
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#144726] tracking-tight">{data.title}</h1>
          <p className="mt-2 inline-flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-[#C6A35B]" /> {data.location}</p>
        </div>
      </section>

      <section className="pb-14 md:pb-20 bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              {/* Gallery */}
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                {img && <img src={img} alt={data.title} className="h-[300px] w-full object-cover md:h-[520px]" />}
              </div>
              {data.images?.length > 1 && (
                <div className="mt-3 grid grid-cols-4 gap-3 md:grid-cols-6">
                  {data.images.map((src, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`aspect-[4/3] overflow-hidden rounded-md border ${i === activeImg ? 'border-[#C6A35B]' : 'border-slate-200'}`}>
                      <img src={src} alt={`Gallery ${i + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Stats row */}
              <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Area</p>
                  <p className="mt-1 font-semibold text-[#144726] flex items-center gap-2"><Maximize2 className="h-4 w-4 text-[#C6A35B]" /> {formatArea(data.area_sqft)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Type</p>
                  <p className="mt-1 font-semibold text-[#144726] flex items-center gap-2"><Building2 className="h-4 w-4 text-[#C6A35B]" /> {propertyTypeLabel(data.property_type)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4 col-span-2 md:col-span-1">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Indicative rent</p>
                  <p className="mt-1 font-semibold text-[#144726]">{formatBudgetRange(data.budget_min, data.budget_max)}</p>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-[#144726]">About this property</h2>
                <p className="mt-4 text-slate-700 leading-relaxed">{data.description}</p>
              </div>

              {data.amenities?.length > 0 && (
                <div className="mt-10">
                  <h3 className="font-display text-2xl font-semibold text-[#144726]">Amenities</h3>
                  <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {data.amenities.map((a) => (
                      <li key={a} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-[#C6A35B]" /> {a}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Enquiry */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">Enquire about this property</p>
                <p className="mt-2 font-display text-xl font-semibold text-[#144726]">Get pricing, floor plan & site visit slots</p>
                <form onSubmit={submitEnquiry} className="mt-5 space-y-4" data-testid="property-enquiry-form">
                  <div>
                    <Label htmlFor="e-name">Name *</Label>
                    <Input id="e-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="property-enquiry-name" />
                  </div>
                  <div>
                    <Label htmlFor="e-email">Email *</Label>
                    <Input id="e-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="property-enquiry-email" />
                  </div>
                  <div>
                    <Label htmlFor="e-phone">Phone *</Label>
                    <Input id="e-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="property-enquiry-phone" />
                  </div>
                  <div>
                    <Label htmlFor="e-msg">Message</Label>
                    <Textarea id="e-msg" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="property-enquiry-message" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="property-enquiry-submit">
                    {submitting ? 'Sending…' : <>Request Details <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </form>
                <div className="mt-6 border-t border-slate-100 pt-5 text-sm text-slate-700 space-y-3">
                  <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-2 hover:text-[#144726]"><Phone className="h-4 w-4 text-[#C6A35B]" /> {SITE.phone}</a>
                  <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-[#144726]"><Mail className="h-4 w-4 text-[#C6A35B]" /> {SITE.email}</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
